import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { KeyRound, Mail, Smartphone } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { useApi, useDocumentTitle } from "../../lib/hooks";
import { signInUrl, useSession, workspaceHome } from "../../lib/session";
import { humanize } from "../../lib/format";
import { LogoMark } from "../../components/Logo";
import { Badge, Button, ErrorNote, Notice, TextArea, TextInput } from "../../components/ui";
import { HumanCheck } from "../../components/HumanCheck";
import { useLang } from "../../lib/i18n";

const ERRORS: Record<string, [string, string]> = {
  expired_login: ["That sign-in attempt expired. Please try again.", "साइन इन का समय ख़त्म हो गया। कृपया फिर से कोशिश करें।"],
  signin_failed: ["Sign-in could not be completed. Please try again.", "साइन इन पूरा नहीं हो पाया। कृपया फिर से कोशिश करें।"],
  identity_in_use: ["That sign-in method is already linked to a different Seva Sangrah account.", "यह साइन इन तरीक़ा पहले से किसी दूसरे सेवा संग्रह खाते से जुड़ा है।"],
  privileged_link_blocked: ["This email belongs to an institutional account. Sign in with its registered method.", "यह ईमेल किसी संस्था के खाते का है। उसी तरीक़े से साइन इन करें जो उसके लिए दर्ज है।"],
  account_inactive: ["This account is not active. Contact Seva Sangrah if you believe this is a mistake.", "यह खाता चालू नहीं है। अगर आपको लगता है कि यह ग़लती है, तो सेवा संग्रह से संपर्क करें।"],
  access_denied: ["Sign-in was cancelled.", "साइन इन रद्द कर दिया गया।"],
};

function GoogleG() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden>
      <path fill="#4285F4" d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.2h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2.1-1.9 3.4-4.7 3.4-8Z" />
      <path fill="#34A853" d="M12 23c3 0 5.4-1 7.2-2.7l-3.5-2.7c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.6H2.2v2.8A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.8 14.1a6.6 6.6 0 0 1 0-4.2V7.1H2.2a11 11 0 0 0 0 9.8l3.6-2.8Z" />
      <path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.1-3.1A11 11 0 0 0 2.2 7.1l3.6 2.8C6.7 7.3 9.1 5.4 12 5.4Z" />
    </svg>
  );
}

export default function SignIn() {
  const { tx, lang } = useLang();
  useDocumentTitle(tx("Sign in", "साइन इन"));
  const { user, config, refresh } = useSession();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo") || undefined;
  const err = params.get("error");
  const personas = useApi(config?.auth.demo ? "/auth/demo/personas" : null);
  const [demoError, setDemoError] = useState<ApiError | null>(null);

  if (user) return <Navigate to={returnTo || workspaceHome(user.workspace)} replace />;

  const demo = async (id: string) => {
    setDemoError(null);
    try {
      await api.post("/auth/demo", { userId: id });
      await refresh();
    } catch (e) {
      setDemoError(e as ApiError);
    }
  };

  const a = config?.auth;
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-paper">
      <div aria-hidden className="absolute -left-40 top-20 h-[460px] w-[460px] rounded-full bg-[radial-gradient(closest-side,rgba(232,131,58,0.18),transparent)] blur-3xl" />
      <div aria-hidden className="absolute -right-40 bottom-0 h-[460px] w-[460px] rounded-full bg-[radial-gradient(closest-side,rgba(53,104,82,0.18),transparent)] blur-3xl" />
      <div className="relative mx-auto flex max-w-md flex-col px-5 py-16 sm:py-24">
        <LogoMark className="mx-auto h-14 w-14" />
        <h1 className="mt-6 text-center text-[28px] font-semibold tracking-tight">{tx("Sign in to Seva Sangrah", "सेवा संग्रह में साइन इन करें")}</h1>
        <p className="mt-2 text-center text-[14.5px] text-ink-500">
          {tx("New here? Signing in creates your citizen account. One account, however you sign in.", "पहली बार आए हैं? साइन इन करते ही आपका खाता बन जाएगा। आप किसी भी तरीक़े से साइन इन करें, खाता एक ही रहेगा।")}
        </p>
        <div className="card mt-10 space-y-3 p-6">
          {err && <Notice tone="error">{ERRORS[err]?.[lang === "hi" ? 1 : 0] || tx("Sign-in did not complete.", "साइन इन पूरा नहीं हुआ।")}</Notice>}
          {!a?.configured && (
            <Notice tone="warn" title={tx("Sign-in is not configured for this environment.", "इस साइट पर अभी साइन इन चालू नहीं है।")}>
              {tx("Ask the administrator to connect the identity provider (Amazon Cognito or Microsoft Entra).", "एडमिन से कहिए कि साइन इन की सेवा (Amazon Cognito या Microsoft Entra) जोड़ दें।")}
            </Notice>
          )}
          {a?.google && (
            <a href={signInUrl("google", returnTo)} className="flex h-12 w-full items-center justify-center gap-3 rounded-full border hairline bg-white text-[14.5px] font-medium text-ink-900 transition hover:bg-ink-50">
              <GoogleG /> {tx("Continue with Google", "Google से आगे बढ़ें")}
            </a>
          )}
          {a?.email && (
            <a href={signInUrl("email", returnTo)} className="flex h-12 w-full items-center justify-center gap-3 rounded-full bg-ink-900 text-[14.5px] font-medium text-white transition hover:bg-ink-800">
              <Mail className="h-[18px] w-[18px]" /> {tx("Continue with email", "ईमेल से आगे बढ़ें")}
            </a>
          )}
          {a?.phone && (
            <a href={signInUrl("phone", returnTo)} className="flex h-12 w-full items-center justify-center gap-3 rounded-full border hairline bg-white text-[14.5px] font-medium text-ink-900 transition hover:bg-ink-50">
              <Smartphone className="h-[18px] w-[18px]" /> {tx("Continue with phone", "फ़ोन नंबर से आगे बढ़ें")}
            </a>
          )}
          <p className="pt-2 text-center text-[12px] leading-relaxed text-ink-400">
            {tx(
              "Email sign-in sends you a one-time code; you can also set a password and recover it from the same screen. Institutional roles additionally verify with an authenticator app.",
              "ईमेल से साइन इन करने पर आपको एक बार इस्तेमाल होने वाला कोड भेजा जाता है; चाहें तो पासवर्ड भी बना सकते हैं और उसी स्क्रीन से उसे वापस पा सकते हैं। संस्थाओं वाले खातों को एक ऑथेंटिकेटर ऐप से भी पुष्टि करनी होती है।",
            )}
          </p>
          <p className="text-center text-[12.5px] text-ink-500">
            {tx("Lost your authenticator or locked out?", "ऑथेंटिकेटर खो गया या खाता खुल नहीं रहा?")}{" "}
            <Link to="/recover" className="underline">
              {tx("Request access recovery", "खाता वापस पाने की अर्ज़ी दें")}
            </Link>
          </p>
        </div>

        {a?.demo && (
          <div className="card mt-6 p-6">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-saffron-600" />
              <div className="text-[14px] font-semibold">{tx("Demo personas", "डेमो वाले खाते")}</div>
              <Badge tone="saffron">{tx("Synthetic", "नक़ली")}</Badge>
            </div>
            <p className="mt-1 text-[12.5px] text-ink-500">{tx("Available only in the demonstration environment.", "सिर्फ़ डेमो साइट पर उपलब्ध।")}</p>
            <ul className="mt-4 max-h-80 space-y-1.5 overflow-y-auto">
              {(personas.data?.personas || []).map((p: any) => (
                <li key={p.id}>
                  <button onClick={() => demo(p.id)} className="flex w-full items-center justify-between rounded-xl border hairline bg-white px-3.5 py-2.5 text-left text-[13px] hover:border-ink-300 cursor-pointer">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-ink-400">{humanize(p.role)}</span>
                  </button>
                </li>
              ))}
            </ul>
            <ErrorNote className="mt-3" error={demoError} />
          </div>
        )}
      </div>
    </div>
  );
}

export function SignInButton({ children = "Sign in" }: { children?: React.ReactNode }) {
  return (
    <Button onClick={() => (window.location.href = signInUrl(undefined, window.location.pathname))} variant="secondary">
      {children}
    </Button>
  );
}

/** MA01-02 / EDGE-02: a logged, human-verified path back in. Never a bare email reset for privileged roles. */
export function RecoverAccess() {
  const { tx } = useLang();
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [human, setHuman] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  return (
    <div className="mx-auto max-w-lg px-5 py-16">
      <h1 className="text-[28px] font-semibold tracking-tight">{tx("Recover access", "खाता वापस पाएँ")}</h1>
      <p className="mt-2 text-[14.5px] leading-relaxed text-ink-500">
        {tx(
          "If you can still sign in with your email or Google but lost your authenticator, use your recovery codes on the verification screen. If that is not possible, tell us here. An administrator verifies your identity before resetting anything, and every step is recorded.",
          "अगर आप ईमेल या Google से साइन इन कर पा रहे हैं पर ऑथेंटिकेटर खो गया है, तो पुष्टि वाली स्क्रीन पर अपने रिकवरी कोड डालिए। अगर ऐसा नहीं हो पा रहा, तो यहाँ बताइए। कुछ भी बदलने से पहले एडमिन आपकी पहचान की पुष्टि करेगा, और हर क़दम दर्ज होगा।",
        )}
      </p>
      {done ? (
        <div className="card mt-6 p-6 text-[14px]">{done}</div>
      ) : (
        <form
          className="card mt-6 space-y-4 p-6"
          onSubmit={async (e) => {
            e.preventDefault();
            setPending(true);
            setErr(null);
            try {
              const r = await api.post("/api/recovery-requests", { email, description, humanCheckToken: human });
              setDone(r.message);
            } catch (e2) {
              setErr(e2 as ApiError);
            } finally {
              setPending(false);
            }
          }}
        >
          <TextInput label={tx("Email on your account", "आपके खाते का ईमेल")} type="email" required value={email} onChange={setEmail} error={err?.fields.email} />
          <TextArea
            label={tx("What happened?", "क्या हुआ?")}
            required
            value={description}
            onChange={setDescription}
            hint={tx("At least 20 characters. Don't include passwords or codes.", "कम से कम 20 अक्षर। पासवर्ड या कोड मत लिखिए।")}
            error={err?.fields.description}
          />
          <HumanCheck onToken={setHuman} />
          <ErrorNote error={err} />
          <Button type="submit" loading={pending}>
            {tx("Send request", "अर्ज़ी भेजें")}
          </Button>
        </form>
      )}
    </div>
  );
}
